import * as React from "react";
import { useEffect, useState, useRef } from "react";
import { Camera } from "./Camera";
import { AllowedFileType, Client, ContractPermissions, PhotoContractState, UserValidationResponse } from "../services/apiClient";
import { Typography, Button, Paper, LinearProgress, CircularProgress } from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { PreviewImage } from "./PreviewImage";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";


const client = new Client();

export const Main = () => {
    const [ContractState, setContractState] = useState<PhotoContractState>(null);
    const [UserValidation, setUserValidation] = useState<UserValidationResponse>(new UserValidationResponse());
    const [ContractError, setContractError] = useState<string>(null);
    const [CurrentFeature, setCurrentFeature] = useState<string>(""); // "camera" | "file" | "preview"
    const [ValidationIsSuccess, setValidationIsSuccess] = useState<boolean>(false);
    const [UploadProgress, setUploadProgress] = useState<number | null>(null);
    const [InstructionMessage, setInstructionMessage] = useState<string>("");
    const [FileUploaded, setFileUploaded] = useState<boolean>(false);
    const [SelectedFileName, setSelectedFileName] = useState<string | null>(null);
    const [ValidationResponseMessage, setValidationResponseMessage] = useState<string>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [AllowedFileExtensions, setAllowedFileExtensions] = useState<string>(""); // Liste des fichiers autorisés pour l'input

    useEffect(() => {
        HandleUrl();
    }, []);

    const HandleUrl = () => {
        const params = new URLSearchParams(window.location.search);
        const uid = params.get("uid");
        if (uid) {
            HandleUid(uid);
        } else {
            setContractError("Aucun Contrat dans la Requête.");
        }
    };
 // 🔹 Définir dynamiquement les types de fichiers autorisés
    const getAllowedFileExtensions = () => {
        if (!ContractState?.allowedFileTypes || ContractState.allowedFileTypes.length === 0) {
            return "";
        }
        return ContractState.allowedFileTypes
            .map(type => {
                switch (type) {
                    case AllowedFileType.JPG: return "image/jpeg";
                    case AllowedFileType.PNG: return "image/png";
                    case AllowedFileType.JPEG: return "image/jpeg";
                    case AllowedFileType.PDF: return "application/pdf";
                    default: return "";
                }
            })
            .filter(Boolean)
            .join(",");
    };

    const HandleUid = async (uid: string, isPageLoad: boolean = true) => {
        setIsLoading(true); // Activer le loader
        try {
            const ContractState: PhotoContractState = await client.contract_GetContract(uid);
            if (!ContractState || ContractState.contractUid == null) {
                return setContractError("Fichier déjà importé ou lien invalide");
            }
            if (isPageLoad && ContractState.userEndValidated === true) {
                return setContractError("Fichier déjà importé.");
            }

            setContractState(ContractState);
            setInstructionMessage(ContractState.message || "Veuillez suivre les instructions.");
            setUserValidation(new UserValidationResponse({ idReq: uid }));

            // 🎯 Génération dynamique des extensions de fichiers autorisés
            const fileExtensions = ContractState.allowedFileTypes
            ?.map(ft => AllowedFileType[ft])
            .filter(ext => !!ext) // Supprime les valeurs nulles
            .map(ext => ext.toLowerCase()) // Convertit en minuscule
            .join(",");

            setAllowedFileExtensions(fileExtensions ? `.${fileExtensions.replace(/,/g, ",.")}` : "");

            if (!isPageLoad) {
                setValidationResponseMessage("Fichier importé");
                setValidationIsSuccess(true);
            }
        } catch (error) {
            setContractError("Erreur lors de la récupération du contrat.");
        } finally {
            setIsLoading(false); // Désactiver le loader
        }
    };

    const HandlePicture = (Image64: string) => {
        setUserValidation((prevState) =>
            new UserValidationResponse({ ...prevState, b64EncodedImage: Image64 })
        );
        setFileUploaded(true);
        setValidationResponseMessage(null);
        setValidationIsSuccess(false);
        setCurrentFeature("preview");
    };

    const HandleFileSelection = (file: File | null) => {
        setValidationResponseMessage(""); // 🔹 Réinitialise le message d'erreur
        if (!file) {
            setValidationResponseMessage("Aucun fichier sélectionné.");
            return;
        }

        const allowedMimeTypes = getAllowedFileExtensions().split(",");

        // Vérification stricte des types MIME autorisés
        if (!allowedMimeTypes.some(type => file.type === type)) {
            setValidationResponseMessage("Ce type de fichier n'est pas autorisé.");
            setValidationIsSuccess(false);
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setValidationResponseMessage("Le fichier est trop volumineux. Taille maximale : 5 Mo.");
            setValidationIsSuccess(false);
            return;
        }

        const reader = new FileReader();
        reader.onloadstart = () => setUploadProgress(0);
        reader.onprogress = (e) => e.lengthComputable && setUploadProgress(Math.round((e.loaded / e.total) * 100));
        reader.onloadend = () => {
            if (!reader.result) {
                setValidationResponseMessage("Erreur lors de la lecture du fichier.");
                return;
            }

            const base64 = reader.result as string;
            setSelectedFileName(file.name);
            setUserValidation(prevState => new UserValidationResponse({
                ...prevState,
                b64EncodedImage: base64
            }));
        };

        reader.readAsDataURL(file);
    };

    const HandleFileValidation = async () => {
        setIsLoading(true); // Activer le loader
        try {
            if (!UserValidation.b64EncodedImage || !SelectedFileName) {
                setValidationResponseMessage("Aucun fichier à valider.");
                setValidationIsSuccess(false);
                return;
            }

            const result = await client.contract_UpdateContract(UserValidation);

            if (result.success) {
                setValidationResponseMessage("Fichier importé avec succès. Santé+ vous remercie !");
                setValidationIsSuccess(true);
                setFileUploaded(true);
            } else {
                setValidationResponseMessage(result.message || "Erreur lors de l'importation.");
                setValidationIsSuccess(false);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Erreur inconnue.";
            setValidationResponseMessage(`Erreur lors de l'envoi : ${errorMessage}`);
            setValidationIsSuccess(false);
        } finally {
            setIsLoading(false); // Désactiver le loader
        }
    };

    const ValidateContract = async () => {
        setIsLoading(true); // Activer le loader
        try {
            const result = await client.contract_UpdateContract(UserValidation);
            if (result.success) {
                setFileUploaded(true);
                setCurrentFeature("");
                setValidationResponseMessage("Fichier importé avec succès. Santé+ vous remercie !");
                setValidationIsSuccess(true);
            } else {
                setValidationResponseMessage(result.message || "Erreur lors de la validation");
                setValidationIsSuccess(false);
            }
        } catch (error) {
            setValidationResponseMessage("Erreur inconnue lors de la validation.");
        } finally {
            setIsLoading(false); // Désactiver le loader
        }
    };

    const fileInputRef = useRef<HTMLInputElement>(null);

    const ResetState = () => {
        setFileUploaded(false);
        setSelectedFileName(null);
        setUploadProgress(null);
        setValidationResponseMessage(null);
        setValidationIsSuccess(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };
    return (
        <div>
            {isLoading && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.5)", // Overlay gris semi-transparent
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999, // S'assurer que le loader est au-dessus de tout
                    }}
                >
                    <CircularProgress style={{ color: "white" }} />
                </div>
            )}

            {/* Logo */}
            <div style={{ textAlign: "center", marginTop: "8vh" }}>
                <img src="/images/cropped-sante_rouge.png" alt="Logo Santé Plus" style={{ maxWidth: "18em" }} />
            </div>

            <div style={{ padding: "20px", maxWidth: "600px", margin: "0 auto", textAlign: "center" }}>
                {ContractError ? (
                    <div style={{ margin: "20px auto", padding: "10px", textAlign: "center", backgroundColor: "#ffe6e6", color: "#d32f2f", fontWeight: "bold" }}>
                        {ContractError}
                    </div>
                ) : CurrentFeature === "camera" ? (
                    <Camera OnReturn={() => setCurrentFeature("")} Hidden={false} OnTakePicture={HandlePicture} />
                ) : CurrentFeature === "preview" ? (
                    <PreviewImage imageSrc={UserValidation.b64EncodedImage} onRetake={() => setCurrentFeature("camera")} onValidate={ValidateContract} />
                ) : (
                    <>
                        {!FileUploaded && (
                            <>
                                <Typography variant="h6" color="text.secondary" style={{ width: "80%", maxWidth: "300px", fontSize: "16px", fontWeight: "bold", height: "60px", textAlign: "center", margin: "20px auto" }}>
                                    {InstructionMessage}
                                </Typography>

                                {ContractState?.permissions?.some((permission) => permission === ContractPermissions.TakePhoto) && (
                                    <div style={{ marginBottom: "20px" }}>
                                        <Button variant="contained" startIcon={<CameraAltIcon />} onClick={() => setCurrentFeature("camera")} style={{ width: "100%", maxWidth: "300px", fontSize: "16px", fontWeight: "bold", height: "60px", backgroundColor: "#003172" }}>
                                            Prendre une Photo
                                        </Button>
                                    </div>
                                )}

                                {ContractState?.permissions?.some((permission) => permission === ContractPermissions.UploadDocument) && (
                                    <div style={{ marginBottom: "20px" }}>
                                        <input ref={fileInputRef} type="file" accept={AllowedFileExtensions} style={{ display: "none" }} onChange={(e) => HandleFileSelection(e.target.files?.[0] || null)} />

                                        <Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => fileInputRef.current?.click()} style={{ width: "100%", maxWidth: "300px", fontSize: "16px", fontWeight: "bold", height: "60px", backgroundColor: "#E54235" }}>
                                            Importer un Document
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}

                        {SelectedFileName && !FileUploaded && (
                            <>
                                <Typography variant="subtitle1" style={{ fontWeight: "bold", marginTop: "20px" }}>{SelectedFileName}</Typography>
                                <div style={{ display: "flex", alignItems: "center", marginTop: "10px", width: "100%", maxWidth: "300px", marginInline: "auto" }}>
                                    <LinearProgress variant="determinate" value={UploadProgress || 0} style={{ flex: 1, height: "10px", borderRadius: "5px", marginRight: "10px" }} />
                                    <Button variant="contained" onClick={ResetState} style={{ width: "100px", height: "40px", backgroundColor: "#f44336", color: "#fff", padding: 0 }}>
                                        <DeleteIcon />
                                    </Button>
                                </div>
                                <div style={{ display: "flex", justifyContent: "center", marginTop: "10px" }}>
                                    <Button variant="contained" startIcon={<CheckCircleIcon />} onClick={HandleFileValidation} style={{ width: "100%", maxWidth: "300px", fontSize: "16px", fontWeight: "bold", height: "40px", backgroundColor: "#4caf50", color: "#fff" }}>
                                        Envoyer
                                    </Button>
                                </div>
                            </>
                        )}
                    </>
                )
                }
                {ValidationResponseMessage && (
                    <div style={{ margin: "20px auto", padding: "10px", textAlign: "center", backgroundColor: ValidationIsSuccess ? "#e8f5e9" : "#ffebee", color: ValidationIsSuccess ? "#388e3c" : "#d32f2f", fontWeight: "bold" }}>
                        {ValidationResponseMessage}
                    </div>
                )}
            </div>
        </div>
    );
};
